import React, {
  useState,
  useRef,
  useContext,
  useEffect,
  useCallback,
} from 'react';

import styles from './Game.module.scss';
import { SettingsContext } from '../../contexts/settings-provider';
import { database } from '../../utils/firebase';
import { ref, get, set, query, orderByValue, onValue } from 'firebase/database';
import usePrevious from '../../hooks/usePrevious';
import { Navigate } from 'react-router-dom';

// eslint-disable-next-line no-unused-vars
import * as tf from '@tensorflow/tfjs';
import Webcam from 'react-webcam';
import * as Tone from 'tone';
import emojiList from '../../data/emoji.json';

import { MdOutlineLockOpen, MdOutlinePublic } from 'react-icons/md';
import Confetti from 'react-confetti';

import Main from '../../components/Main/Main';
import Button from '../../components/Button/Button';
import Gallery from '../../components/Gallery/Gallery';
import Award from '../../components/Award/Award';
import Logo from '../../components/Logo/Logo';
import classNames from 'classnames';
import Toggle from '../../components/Toggle/Toggle';

import point from '../../assets/audio/point.mp3';
import ProfileLink from '../../components/ProfileLink/ProfileLink';
import Score from '../../components/Score/Score';

const Game = () => {
  const confettiAmount = 0; //200;
  const minRenderTime = 1000 / 60;
  const [settings] = useContext(SettingsContext);

  const [size, setSize] = useState({ width: 640, height: 480 });
  const [mirrored, setMirrored] = useState(false);
  const [drawAr, setDrawAr] = useState(true);

  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [objects, setObjects] = useState([]);
  const previousObjects = usePrevious(objects);

  const [visibleEmojis, setVisibleEmojis] = useState([]);

  const [unlockedEmojis, setUnlockedEmojis] = useState([]);
  const [award, setAward] = useState(false);
  const [showAward, setShowAward] = useState(false);
  const [showGallery, setShowGallery] = useState(false);
  const [confettiCount, setConfettiCount] = useState(0);
  const [showRanking, setShowRanking] = useState(false);
  const [ranking, setRanking] = useState({ ranking: 1, players: 1 });

  const [minScore, setMinScore] = useState(settings.minScore);

  const lastRender = useRef(Date.now());

  const pointAudio = useRef(null);
  const webcamRef = useRef(null);
  const canvasRef = useRef(null);

  const playAudio = (audio) => {
    if (audio && audio.start) {
      Tone.loaded().then(() => {
        audio.start();
      });
    }
  };

  useEffect(() => {
    pointAudio.current = new Tone.Player(point).toDestination();
  }, []);

  useEffect(() => {
    const userData = ref(database, `settings/minScore`);
    onValue(userData, (snapshot) => {
      const cloudMinScore = snapshot.val();
      if (cloudMinScore) {
        setMinScore(cloudMinScore);
      }
    });
  }, [settings.user]);

  useEffect(() => {
    const highscoreRef = query(ref(database, `highscore`), orderByValue());
    onValue(highscoreRef, (snapshot) => {
      const highscoreList = snapshot.val();
      if (highscoreList) {
        const newHighscore = [];
        snapshot.forEach((child) => {
          newHighscore.push({ key: child.key, score: child.val() });
        });

        const players = newHighscore.length;
        let ranking = players;
        newHighscore
          .sort((a, b) => {
            if (a.score > b.score) {
              return -1;
            } else {
              return 1;
            }
          })
          .forEach((entry, i) => {
            if (entry.key === settings.user.uid) {
              ranking = i + 1;
            }
          });
        setRanking({ players: players, ranking: ranking });
      }
    });
  }, [settings.user]);

  useEffect(() => {
    if (settings.user) {
      const userData = ref(database, `game/${settings.user.uid}`);
      get(userData)
        .then((snapshot) => {
          const userUnlocked = snapshot.val();
          const existingUnlocks = [];
          for (const key in userUnlocked) {
            existingUnlocks.push(key);
          }
          setUnlockedEmojis(existingUnlocks);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [settings.user]);

  // Update size
  useEffect(() => {
    const updateSize = () => {
      if (webcamRef.current) {
        setSize({
          width: webcamRef.current.video.videoWidth,
          height: webcamRef.current.video.videoHeight,
        });
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => {
      window.removeEventListener('resize', updateSize);
    };
  }, [running, loading]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = size.width;
      canvas.height = size.height;
    }
    if (size.width > size.height) {
      setMirrored(true);
    } else {
      setMirrored(false);
    }
  }, [size, loading, running]);

  useEffect(() => {
    const initiatePoseDetection = async () => {
      const cocoSsd = require('@tensorflow-models/coco-ssd');
      const model = await cocoSsd.load();
      if (model) {
        setLoading(false);
      }
      detectObjects(model);
    };

    if (running) {
      setLoading(true);
      initiatePoseDetection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  const detectObjects = async (model) => {
    if (Date.now() - lastRender.current >= minRenderTime) {
      const predictions = await model.detect(
        webcamRef.current?.video,
        20,
        minScore ? minScore : 0.4
      );
      const newObjects = predictions.map((prediction) => {
        return prediction;
      });
      setObjects(newObjects);
      lastRender.current = Date.now();
    }
    window.requestAnimationFrame(() => {
      detectObjects(model);
    });
  };

  const unlockEmoji = useCallback(
    (emoji) => {
      if (showAward === false && showGallery === false) {
        const isFound = unlockedEmojis.find(
          (unlockedEmoji) => unlockedEmoji === emoji
        );
        if (!isFound) {
          const newUnlockedEmojis = [...unlockedEmojis];
          newUnlockedEmojis.push(emoji);
          setUnlockedEmojis(newUnlockedEmojis);
          setAward(emoji);
          setShowAward(true);
          playAudio(pointAudio.current);

          const playerRef = ref(database, `game/${settings.user.uid}/${emoji}`);
          set(playerRef, Date.now());

          const highscoreRef = ref(database, `highscore/${settings.user.uid}`);
          set(highscoreRef, newUnlockedEmojis.length);
        }
      }
    },
    [unlockedEmojis, showAward, showGallery, settings.user]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas && !showAward) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, size.width, size.height);

      const newVisibleEmojis = [];
      objects.forEach((object) => {
        const previousMatching =
          previousObjects.filter((e) => e.class === object.class).length > 0;
        const matchingEmoji = emojiList.find(
          (emoji) => emoji.description === object.class
        );

        if (matchingEmoji && previousMatching) {
          newVisibleEmojis.push(matchingEmoji.emoji);
          unlockEmoji(matchingEmoji.emoji);

          if (drawAr === true) {
            const bounding = object.bbox;
            ctx.font = `${
              (Math.max(bounding[2] - bounding[0]) / 2, size.width / 4)
            }px sansserif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(
              matchingEmoji.emoji,
              mirrored
                ? size.width - (bounding[0] + bounding[2] / 2)
                : bounding[0] + bounding[2] / 2,
              bounding[1] + bounding[3] / 2
            );
          }
        } else if (!matchingEmoji) {
          const unknownRef = ref(database, `unknown/${object.class}`);
          set(unknownRef, settings.version);
        }
      });
      setVisibleEmojis(newVisibleEmojis.sort());
    }
  }, [
    drawAr,
    mirrored,
    size,
    previousObjects,
    objects,
    unlockEmoji,
    settings.version,
    showAward,
  ]);

  useEffect(() => {
    if (showAward) {
      window.setTimeout(() => {
        setConfettiCount(0);
      }, 5000);
      setConfettiCount(confettiAmount);
    }
  }, [showAward]);

  const ready = !loading && running;

  if (unlockedEmojis.length === emojiList.length) {
    <Navigate to="/winner" />;
  } else {
    return (
      <Main>
        {ready && (
          <header className={styles.header}>
            <div
              className={styles.score}
              onClick={() => {
                setShowRanking(!showRanking);
              }}
            >
              {showRanking ? (
                <Score
                  icon={MdOutlinePublic}
                  score={ranking.ranking}
                  total={ranking.players}
                  label={'Ranking'}
                />
              ) : (
                <Score
                  icon={MdOutlineLockOpen}
                  score={unlockedEmojis.length}
                  total={emojiList.length}
                  label={'Unlocked'}
                />
              )}
            </div>
            <Logo className={classNames(styles.logo, styles.noMobile)} />
            <ProfileLink className={styles.link} />
          </header>
        )}
        <section
          className={styles.media}
          onPointerDown={(e) => {
            e.preventDefault();
          }}
        >
          <Webcam
            className={classNames(styles.video, {
              [styles.waiting]: !running || loading,
            })}
            ref={webcamRef}
            videoConstraints={{ facingMode: 'environment' }}
            mirrored={mirrored}
            screenshotQuality={1}
            onUserMedia={() => {
              window.setTimeout(() => {
                setRunning(true);
              }, 1000);
            }}
          />
          <canvas className={styles.canvas} ref={canvasRef} />
          {drawAr === false && (
            <div>
              {visibleEmojis.length > 0 && (
                <div className={styles.visible}>
                  {visibleEmojis
                    .filter(function (item, pos) {
                      return visibleEmojis.indexOf(item) === pos;
                    })
                    .map((emoji, i) => {
                      return <div key={`${emoji}-${i}`}>{emoji}</div>;
                    })}
                </div>
              )}
            </div>
          )}
          {loading && (
            <div className={styles.loading}>
              <b>Loading AI...</b>
              <br />
              This may take up to 1 minute
            </div>
          )}
        </section>
        <footer className={styles.footer}>
          {ready && (
            <div className={styles.actions}>
              <Button
                onClick={() => {
                  setShowGallery(true);
                }}
              >
                My emojis
              </Button>
              <Toggle
                toggle={() => {
                  setDrawAr(!drawAr);
                }}
                active={drawAr}
                text={'AR'}
              />
            </div>
          )}
        </footer>
        <Award
          emoji={award}
          close={() => {
            setShowAward(false);
          }}
          open={showAward}
        />
        <Gallery
          ranking={ranking}
          list={emojiList}
          unlocked={unlockedEmojis}
          close={() => {
            setShowGallery(false);
          }}
          open={showGallery}
        />
        <div className={styles.confetti}>
          <Confetti
            width={window.innerWidth}
            height={window.innerHeight}
            numberOfPieces={confettiCount}
            colors={['#0000ff', '#f0cc00', '#ffffff']}
            /*
          drawShape={(ctx) => {
            ctx.font = `${40}px sansserif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(award, 0, 0);
          }}
          */
          />
        </div>
      </Main>
    );
  }
};

export default Game;
